/**
 * Notification type constants for ComplyOne.
 * Backend should broadcast these types so the frontend can route and display consistently.
 *
 * Backend contract (Laravel Reverb / Echo):
 * - Channel: workspace.{workspaceId}.users.{userId}
 * - Event: workspace.notification.created
 * - Payload: { id?, title, message, type?, link?, created_at?, data? }
 *   - type: one of NOTIFICATION_TYPES below (policy_created, training_assigned, etc.)
 *   - link: optional route path relative to workspace (e.g. "policy-tracker") or absolute path
 * When to notify:
 *   - Policy created/renewed in workspace → founder & CCO
 *   - Policy overdue / renewal date soon → founder & CCO
 *   - Training assigned → assigned user
 *   - Training completed or assignment changed → CCO & founder
 *   - Vendor onboarding form submitted → founder
 *   - CCO updates vendor status → founder
 */
export const NOTIFICATION_TYPES = {
  // Policy: create/renewal in workspace → notify founder & CCO
  POLICY_CREATED: 'policy_created',
  POLICY_RENEWED: 'policy_renewed',
  // Policy overdue or renewal date approaching → alert founder & CCO
  POLICY_OVERDUE: 'policy_overdue',
  POLICY_RENEWAL_SOON: 'policy_renewal_soon',
  // Training assigned → notify user
  TRAINING_ASSIGNED: 'training_assigned',
  // Training complete or changed → notify CCO & founder
  TRAINING_COMPLETED: 'training_completed',
  TRAINING_ASSIGNMENT_CHANGED: 'training_assignment_changed',
  // Vendor onboarding submitted → notify founder
  VENDOR_ONBOARDING_SUBMITTED: 'vendor_onboarding_submitted',
  // CCO updates vendor status → notify founder
  VENDOR_STATUS_UPDATED: 'vendor_status_updated',
  // Generic
  INFO: 'info',
  SUCCESS: 'success',
  WARNING: 'warning',
}

/** Human-readable labels for notification types (for accessibility/fallback) */
export const NOTIFICATION_TYPE_LABELS = {
  [NOTIFICATION_TYPES.POLICY_CREATED]: 'Policy created',
  [NOTIFICATION_TYPES.POLICY_RENEWED]: 'Policy renewed',
  [NOTIFICATION_TYPES.POLICY_OVERDUE]: 'Policy overdue',
  [NOTIFICATION_TYPES.POLICY_RENEWAL_SOON]: 'Policy renewal soon',
  [NOTIFICATION_TYPES.TRAINING_ASSIGNED]: 'Training assigned',
  [NOTIFICATION_TYPES.TRAINING_COMPLETED]: 'Training completed',
  [NOTIFICATION_TYPES.TRAINING_ASSIGNMENT_CHANGED]: 'Training assignment changed',
  [NOTIFICATION_TYPES.VENDOR_ONBOARDING_SUBMITTED]: 'Vendor onboarding submitted',
  [NOTIFICATION_TYPES.VENDOR_STATUS_UPDATED]: 'Vendor status updated',
  [NOTIFICATION_TYPES.INFO]: 'Information',
  [NOTIFICATION_TYPES.SUCCESS]: 'Success',
  [NOTIFICATION_TYPES.WARNING]: 'Warning',
}

/** Default route path by type (relative to workspace). Backend can override via notification.link */
export const NOTIFICATION_DEFAULT_ROUTES = {
  [NOTIFICATION_TYPES.POLICY_CREATED]: 'policy-tracker',
  [NOTIFICATION_TYPES.POLICY_RENEWED]: 'policy-tracker',
  [NOTIFICATION_TYPES.POLICY_OVERDUE]: 'policy-tracker',
  [NOTIFICATION_TYPES.POLICY_RENEWAL_SOON]: 'policy-tracker',
  [NOTIFICATION_TYPES.TRAINING_ASSIGNED]: 'compliance-training',
  [NOTIFICATION_TYPES.TRAINING_COMPLETED]: 'compliance-training',
  [NOTIFICATION_TYPES.TRAINING_ASSIGNMENT_CHANGED]: 'compliance-training',
  [NOTIFICATION_TYPES.VENDOR_ONBOARDING_SUBMITTED]: 'vendor-management',
  [NOTIFICATION_TYPES.VENDOR_STATUS_UPDATED]: 'vendor-management',
}
